import { memo } from 'react'
import { AlertTriangle, Ban, Bell, Clock3, TrendingUp } from 'lucide-react'
import { formatPercent } from '@/utils/format-currency'
import { formatDealValue } from '@/utils/dealDisplay'
import { cn } from '@/utils/cn'
import { Tooltip } from '@/components/ui/Tooltip'
import { getDealAvatarFallback } from '@/utils/dealDisplay'
import { KanbanMoveStageMenu } from '@/components/Deals/KanbanMoveStageMenu'
import { getDealMeetingSchedule, getDealRiskV2, getNextStepV2, type DealRiskIconKind } from '@/components/DealsV2/kanbanCardV2Utils'
import { KanbanCardMeetingChip, KanbanCardMeetingRelative, KanbanCardNextStepChip } from '@/components/DealsV2/KanbanCardMeetingChip'
import type { Deal, DealStage } from '@/types'

function RiskIcon({ kind }: { kind: DealRiskIconKind }) {
  const props = { size: 13 as const }
  switch (kind) {
    case 'ban':
      return <Ban {...props} />
    case 'clock':
      return <Clock3 {...props} />
    case 'alert':
      return <AlertTriangle {...props} />
    case 'trending':
      return <TrendingUp {...props} />
  }
}

export type KanbanCardV2Props = {
  deal: Deal
  stageColor: string
  onClick: () => void
  areValuesVisible?: boolean
  onMoveStage?: (dealId: string, newStage: DealStage) => void
  isDragging?: boolean
}

function KanbanCardV2Component({
  deal,
  stageColor,
  onClick,
  areValuesVisible = false,
  onMoveStage,
  isDragging,
}: KanbanCardV2Props) {
  const risk = getDealRiskV2(deal)
  const meeting = getDealMeetingSchedule(deal)
  const nextStep = getNextStepV2(deal)
  const redundant = deal.nome.toLowerCase().includes(deal.empresaNome.toLowerCase())

  return (
    <article
      onClick={onClick}
      className={cn(
        'deals-v2-card group/card relative flex cursor-pointer flex-col gap-3 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-3.5 shadow-sm',
        risk.tone === 'info' &&
          'border-[var(--brand-primary)]/35 bg-[var(--brand-primary)]/[0.05] ring-1 ring-[var(--brand-primary)]/15',
        risk.tone === 'warning' && 'border-warning/30 bg-warning/[0.02]',
        risk.tone === 'danger' && 'border-danger/30 bg-danger/[0.02]',
        isDragging && 'scale-[0.98] opacity-60 ring-2 ring-[var(--brand-primary)]/20',
      )}
    >
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0 flex-1'>
          <p className='truncate text-[13px] font-semibold text-text-primary'>{deal.empresaNome}</p>
          {!redundant && (
            <p className='mt-0.5 truncate text-[11px] text-text-muted'>{deal.nome}</p>
          )}
        </div>
        <div className='flex shrink-0 items-center gap-1'>
          {onMoveStage && <KanbanMoveStageMenu deal={deal} onMoveStage={onMoveStage} />}
          {risk.tone && risk.icon && (
            <Tooltip content={risk.label} position='bottom'>
              <span
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-md border',
                  risk.tone === 'danger'
                    ? 'border-danger/20 bg-danger/10 text-danger'
                    : risk.tone === 'info'
                      ? 'border-[var(--brand-primary)]/25 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]'
                      : 'border-warning/20 bg-warning/10 text-warning',
                )}
              >
                {deal.financeAwaitingCloser ? <Bell size={13} /> : <RiskIcon kind={risk.icon} />}
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <div className='flex items-end justify-between gap-3'>
        <div className='min-w-0 flex-1'>
          <span className='mb-1 block text-[10px] font-medium text-text-muted'>Valor previsto</span>
          <p className='text-[15px] font-bold tabular-nums leading-none text-text-primary'>
            {formatDealValue(deal.valor, areValuesVisible, true)}
          </p>
        </div>
        <div className='flex shrink-0 flex-col items-end gap-1'>
          {meeting ? <KanbanCardMeetingRelative meeting={meeting} /> : null}
          <span className='text-[11px] font-semibold tabular-nums text-text-primary'>
            {formatPercent(deal.probabilidade, 0)}
          </span>
          <div className='h-1.5 w-14 overflow-hidden rounded-full bg-[var(--bg-dark)]'>
            <div
              className='h-full rounded-full transition-all duration-300'
              style={{
                width: `${deal.probabilidade}%`,
                backgroundColor: stageColor,
              }}
            />
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-2 border-t border-[var(--border)] pt-2.5'>
        <div className='flex items-center justify-between gap-2'>
          {meeting ? (
            <KanbanCardMeetingChip meeting={meeting} />
          ) : (
            <KanbanCardNextStepChip label={nextStep} />
          )}
          <Tooltip content={deal.closerNome || 'Sem closer'} position='bottom'>
            <span className='inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-dark)] text-[9px] font-bold uppercase text-text-secondary'>
              {getDealAvatarFallback(deal.closerNome, deal.closerAvatar)}
            </span>
          </Tooltip>
        </div>
        <div className='flex items-center justify-between text-[10px] text-text-muted'>
          <span className='inline-flex items-center gap-1'>
            <Clock3 size={10} />
            {deal.driftDays > 0 ? `${deal.driftDays}d na etapa` : 'Entrada recente'}
          </span>
          {risk.label ? (
            <span
              className={cn(
                'max-w-[110px] truncate',
                risk.tone === 'danger'
                  ? 'text-danger'
                  : risk.tone === 'info'
                    ? 'font-semibold text-[var(--brand-primary)]'
                    : 'text-warning',
              )}
            >
              {risk.label}
            </span>
          ) : null}
        </div>
      </div>
    </article>
  )
}

export const KanbanCardV2 = memo(KanbanCardV2Component)
